import { ASSETS_URL, FOLDERS_URL, PROJECTS_URL } from '../../../../config/urls';
import { isItemNamesTakenInFolderError } from '../../../../errors/assetUploads';
import ResponseError from '../../../errors/ResponseError';
import ItemNamesTakenInFolderError from '../errors/ItemNamesTakenInFolderError';
import InsufficientStorageError, { isInsufficientStorageError } from '../errors/InsufficientStorageError';

import type Api from '../../../../services/Api';
import type ReduxTanStackSync from '../../../services/ReduxTanStackSync';
import type { PaginatedFetchResult, PaginationJSON } from '../../../types';
import type { AssetJSON } from '../models/asset';
import type AssetFilters from '../models/filtersAndSorts/assetFilters';
import type ItemSorting from '../models/filtersAndSorts/itemSorting';
import type { SortDirection } from '../models/filtersAndSorts/types';
import type { FolderJSON } from '../models/folder';
import { type AssetRoundJSON } from '../models/assetRound';
import { type ProjectJSON } from '../models/projectJson';

import AssetImportService from '../../backgroundTasks/services/AssetsImportService/AssetsImportService';
import defaultAssetImportService from '../../backgroundTasks/services/AssetsImportService';
import type { OnFinishedCallback, OnAssetImportedCallback } from '../../backgroundTasks/models/ImportTask';

type AssetSystemParams = {
	api: Api,
	sync: ReduxTanStackSync,
	importService?: AssetImportService
}

type AssetsFetchParams = {
	projectID: number,
	folderID: number | null,
	page: number,
	filters?: AssetFilters,
	sorting?: ItemSorting,
	deep?: boolean
};

type FolderFetchParams = {
	projectID: number,
	folderID: number | null,
	filters?: AssetFilters
};

export type AssetsFetchResult = PaginatedFetchResult<AssetJSON>;
export type FoldersFetchResult = FolderJSON[];
export type AssetRoundsFetchResult = AssetRoundJSON[];

export type MoveItemsParams = {
	folderIds: number[],
	assetIds: number[],
	destProjectId: number,
	destFolderId: number | null
}

export type MoveItemsResponse = {
	destination: FolderJSON | ProjectJSON,
	takenItemNames: string[]
}

export type ImportFile = {
	name: string,
	link: string
}

export type ImportAssetsParams = {
	files: ImportFile[],
	projectID: number,
	folderID: number | null
}

export type ImportAssetsResponse = {
	import_id: string
}

export const ASSETS_PER_PAGE = 100;

export default class AssetSystem {
	private api: Api;
	private sync: ReduxTanStackSync;
	private assetImportService: AssetImportService;

	constructor( { api, sync, importService = defaultAssetImportService }: AssetSystemParams ) {
		this.api = api;
		this.sync = sync;
		this.assetImportService = importService;
	}

	async importAssets(
		{ files, projectID, folderID }: ImportAssetsParams,
		onAssetImported?: OnAssetImportedCallback,
		onFinished?: OnFinishedCallback
	): Promise<void> {
		const url = `${ASSETS_URL}/import`;
		const { import_id } = await (
			this.api.post( url, { files, project_id: projectID, folder_id: folderID } ) as Promise<ImportAssetsResponse>
		);

		this.assetImportService?.registerAssetsImport(
			import_id, onAssetImported, onFinished
		);
	}

	async fetchAsset( assetID: number ): Promise<AssetJSON> {
		const url = `${ASSETS_URL}/${assetID}`;

		const { response } = await (
			this.api.get( url ) as Promise<{ response: AssetJSON }>
		);

		this.sync.asset( response );

		return response;
	}

	async fetchAssets( {
		projectID,
		folderID,
		page,
		filters,
		sorting,
		deep = false
	}: AssetsFetchParams ): Promise<AssetsFetchResult> {
		const baseUrl = this.baseUrlFor( { projectID, folderID } );
		const queryParams = this.assetsQueryParamsFor( { page, filters, sorting, deep } );
		const assetsUrl = `${baseUrl}/assets?${queryParams}`;
		const { response, pagination } = await (
			this.api.get( assetsUrl ) as Promise<{ response: AssetJSON[], pagination: PaginationJSON }>
		);

		// As long as the Redux's assets store from the old architecture is still used, we have to update
		// it with the assets we fetch with the new architecture.
		this.sync.assets( response, { projectID, replaceAll: page === 1 } );

		// TODO: backend should include the project_id attribute with each asset
		// We are temporarily emulating it here
		return {
			items: response.map( asset => ( { ...asset, project_id: projectID } ) ),
			pagination
		};
	}

	async fetchFolders( { projectID, folderID, filters }: FolderFetchParams ): Promise<FoldersFetchResult> {
		const baseUrl = this.baseUrlFor( { projectID, folderID } );
		const queryParams = this.foldersQueryParamsFor( { filters } );
		const foldersUrl = `${baseUrl}/folders${queryParams ? `?${queryParams}` : ''}`;

		const { response } = await (
			this.api.get( foldersUrl ) as Promise<{ response: FolderJSON[] }>
		);

		// As long as the Redux's folders store from the old architecture is still used, we have to update
		// it with the folders we fetch with the new architecture.
		this.sync.folders( response, {
			organizerID: folderID || projectID,
			organizerType: folderID ? 'Folder' : 'Project'
		} );

		return response;
	}

	async fetchAssetRounds( { assetID }: { assetID: number } ): Promise<AssetRoundsFetchResult> {
		const url = `${ASSETS_URL}/${assetID}/versions`;

		const { response } = await (
			this.api.get( url ) as Promise<{ response: AssetRoundsFetchResult }>
		);

		return response;
	}

	async fetchMaxRoundNumber( { projectID }: { projectID: number } ): Promise<number | null> {
		const url = `${PROJECTS_URL}/${projectID}/max_round_number`;

		const { response } = await (
			this.api.get( url ) as Promise<{ response: { max_round_number: number | null } }>
		);

		return response.max_round_number;
	}

	async moveItems( {
		folderIds, assetIds, destProjectId, destFolderId
	}: MoveItemsParams ): Promise<MoveItemsResponse> {
		try {
			const { response, meta } = await ( this.api.post(
				`${PROJECTS_URL}/move_items`,
				{
					project_id: destProjectId,
					folder_id: destFolderId,
					assets_ids: assetIds,
					folders_ids: folderIds
				}
			) as Promise<{ response: FolderJSON | ProjectJSON, meta: { duplicate_names: string[] } }> );

			return { destination: response, takenItemNames: meta?.duplicate_names || [] };
		} catch ( error ) {
			if ( error instanceof ResponseError && isItemNamesTakenInFolderError( error ) ) {
				throw ItemNamesTakenInFolderError.fromResponseError( error )
			} else if ( error instanceof ResponseError && isInsufficientStorageError( error ) ) {
				throw InsufficientStorageError.fromResponseError( error );
			}

			throw error;
		}
	}

	async setAssetPriority( assetId: number ) {
		await ( this.api.post( `${ASSETS_URL}/${assetId}/set_rush_priority` ) as Promise<Response> );
		this.sync.assetPrioritySet( assetId );
	}

	async unsetAssetPriority( assetId: number ) {
		await ( this.api.post( `${ASSETS_URL}/${assetId}/unset_rush_priority` ) as Promise<Response> );
		this.sync.assetPriorityUnset( assetId );
	}

	async setAssetRating( assetId: number, rating: number | null ): Promise<AssetJSON> {
		const { response } = await (
			this.api.patch( `${ASSETS_URL}/${assetId}/set_rating`, { rating } ) as Promise<{ response: AssetJSON }>
		);
		this.sync.asset( response );
		return response;
	}

	async changeAssetFileName( assetId: number, projectId: number, newName: string ) {
		const { response } = await (
			this.api.patch(
				`${ASSETS_URL}/${assetId}/update_filename`,
				{ filename: newName }
			) as Promise<{ response: AssetJSON }>
		);

		return { ...response, project_id: projectId };
	}

	async updateAssetAssignees( {
		projectID,
		assetIDs,
		usersToAddIDs,
		usersToRemoveIDs
	}: {
		projectID: number;
		assetIDs: number[];
		usersToAddIDs: number[];
		usersToRemoveIDs: number[];
	} ): Promise<void> {
		await (
			this.api.put( `${ASSETS_URL}/assignees`, {
				project_id: projectID,
				assets_ids: assetIDs,
				users_to_add_ids: usersToAddIDs,
				users_to_remove_ids: usersToRemoveIDs
			} ) as Promise<Response>
		);
	}

	async setAssetsOrder( {
		projectID, assetIds, direction
	}: {
		projectID: number, assetIds: number[], direction: SortDirection
	} ) {
		await (
			this.api.patch(
				`${PROJECTS_URL}/${projectID}/set_assets_order`,
				{ asset_ids: assetIds, order: direction }
			) as Promise<Response>
		);
	}

	private baseUrlFor( { projectID, folderID }: { projectID: number, folderID: number | null } ) {
		return folderID
			? `${FOLDERS_URL}/${folderID}`
			: `${PROJECTS_URL}/${projectID}`;
	}

	private assetsQueryParamsFor( {
		page, filters, sorting, deep
	}: {
		page: number, filters?: AssetFilters, sorting?: ItemSorting, deep: boolean
	} ) {
		const params = new URLSearchParams( {
			page: page.toString(),
			per_page: ASSETS_PER_PAGE.toString()
		} );

		if ( filters ) params.append( 'filters', filters.toJsonString() );
		if ( sorting ) {
			Object
				.entries( sorting.queryParams() )
				.forEach( ( [ key, val ] ) => params.append( key, val ) );
		}
		if ( deep ) params.append( 'deep', 'true' );

		return params.toString();
	}

	private foldersQueryParamsFor( { filters }: { filters?: AssetFilters } ) {
		return filters?.usesSearchTextOrFilterOption
			? new URLSearchParams( { asset_filters: filters.toJsonString() } ).toString()
			: '';
	}
}
